import { useEffect } from 'react'

const FOCUSABLE_INPUT_SELECTOR =
  'input:not([type="button"]):not([type="submit"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]), textarea, select, [contenteditable="true"]'

const OPENS_KEYBOARD_SELECTOR =
  // eslint-disable-next-line max-len
  'input:not([type="button"]):not([type="submit"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([type="hidden"]):not([type="range"]):not([type="color"]):not([readonly]), textarea, [contenteditable="true"]'

const KEYBOARD_OPEN_THRESHOLD_PX = 80
const KEYBOARD_FALLBACK_RATIO = 0.42
const KEYBOARD_SETTLE_MS = 350

const getScrollParent = (element) => {
  let parent = element?.parentElement
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent)
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      parent.scrollHeight > parent.clientHeight
    ) {
      return parent
    }
    parent = parent.parentElement
  }
  return document.scrollingElement || document.documentElement
}

const getLayoutHeight = () =>
  Math.max(window.innerHeight, document.documentElement.clientHeight || 0)

const isCoarsePointer = () =>
  window.matchMedia('(hover: none) and (pointer: coarse)').matches

const getMeasuredKeyboardInset = (vv) => {
  if (!vv) return 0
  return Math.max(0, Math.round(getLayoutHeight() - vv.height - vv.offsetTop))
}

const updateVisualViewportVars = ({ allowFallback = false } = {}) => {
  const vv = window.visualViewport
  if (!vv) {
    document.documentElement.style.setProperty('--vv-height', '100svh')
    document.documentElement.style.setProperty('--keyboard-inset', '0px')
    document.documentElement.classList.remove('keyboard-open')
    return
  }

  const layoutHeight = getLayoutHeight()
  const measuredInset = getMeasuredKeyboardInset(vv)
  let keyboardInset = measuredInset
  let vvHeight = vv.height

  // Some Android WebViews keep visualViewport full-size with overlays-content,
  // so short bottom sheets (e.g. empty select) sit under the keyboard.
  if (
    allowFallback &&
    measuredInset < KEYBOARD_OPEN_THRESHOLD_PX &&
    isCoarsePointer()
  ) {
    keyboardInset = Math.round(layoutHeight * KEYBOARD_FALLBACK_RATIO)
    vvHeight = Math.max(0, layoutHeight - keyboardInset)
  }

  document.documentElement.style.setProperty('--vv-height', `${vvHeight}px`)
  document.documentElement.style.setProperty(
    '--keyboard-inset',
    `${keyboardInset}px`,
  )
  document.documentElement.classList.toggle(
    'keyboard-open',
    keyboardInset > KEYBOARD_OPEN_THRESHOLD_PX,
  )
}

const onViewportChange = () => updateVisualViewportVars()

const scrollFocusedInputIntoView = (target) => {
  if (!target?.matches?.(FOCUSABLE_INPUT_SELECTOR)) return

  const vv = window.visualViewport
  if (!vv) return

  window.setTimeout(() => {
    const rect = target.getBoundingClientRect()
    const visibleTop = vv.offsetTop
    const visibleBottom = vv.offsetTop + vv.height - 16
    if (rect.bottom <= visibleBottom && rect.top >= visibleTop) return

    const scrollParent = getScrollParent(target)
    const delta = rect.bottom - visibleBottom
    if (scrollParent === document.scrollingElement) {
      window.scrollBy({ top: delta, behavior: 'smooth' })
    } else {
      scrollParent.scrollBy({ top: delta, behavior: 'smooth' })
    }
  }, 250)
}

/**
 * Stabilizes Android mobile layout when the soft keyboard opens:
 * - exposes --vv-height / --keyboard-inset CSS vars
 * - falls back to an estimated inset when visualViewport does not shrink
 * - scrolls the focused field into the visual viewport
 */
const useVisualViewportKeyboardFix = () => {
  useEffect(() => {
    updateVisualViewportVars()

    const vv = window.visualViewport
    vv?.addEventListener('resize', onViewportChange)
    vv?.addEventListener('scroll', onViewportChange)
    window.addEventListener('resize', onViewportChange)

    let settleTimer

    const onFocusIn = (event) => {
      const { target } = event
      updateVisualViewportVars()
      scrollFocusedInputIntoView(target)

      if (!target?.matches?.(OPENS_KEYBOARD_SELECTOR)) return

      window.clearTimeout(settleTimer)
      settleTimer = window.setTimeout(() => {
        updateVisualViewportVars({ allowFallback: true })
        scrollFocusedInputIntoView(target)
      }, KEYBOARD_SETTLE_MS)
    }

    const onFocusOut = () => {
      window.clearTimeout(settleTimer)
      // Let focus move to the next field before clearing keyboard insets
      settleTimer = window.setTimeout(() => {
        const active = document.activeElement
        if (!active?.matches?.(OPENS_KEYBOARD_SELECTOR)) {
          updateVisualViewportVars()
        }
      }, 100)
    }

    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)

    return () => {
      window.clearTimeout(settleTimer)
      vv?.removeEventListener('resize', onViewportChange)
      vv?.removeEventListener('scroll', onViewportChange)
      window.removeEventListener('resize', onViewportChange)
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
      document.documentElement.classList.remove('keyboard-open')
      document.documentElement.style.removeProperty('--vv-height')
      document.documentElement.style.removeProperty('--keyboard-inset')
    }
  }, [])
}

export default useVisualViewportKeyboardFix
